import { faCheck as fasCheck, faClock as fasClock } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ReactNode } from "react";
import { Col, Row } from "react-bootstrap";
import { Config } from "@/app/config";
import type { LeadFormProps, LeadFormStepFields } from "@/component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import { BasicLeadForm } from "@/component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import { PRODUCT_REQUISITION_TIME } from "@/component/Mortgage/LeadForm/Steps/LeadForm.data";
import type { ProductRequisitionTime } from "@/component/Mortgage/LeadForm/types";
import { FormCheck, FormDecidable, FormSelect, List } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";

export interface PersonalLoanLeadStepFields extends LeadFormStepFields {
	wantsWithOnlineService?: boolean;
	productRequisitionTime?: ProductRequisitionTime;
	inKHR?: boolean;
}

export interface PersonalLoanLeadProps extends LeadFormProps {
	showDetails?: boolean;
	withInKhr?: boolean;
	withOnlineOption?: boolean;
	withRequisitionTimeOption?: boolean;
	loanAmount?: number;
	maturityYears?: number;
	installment?: number;
	apr?: number;

	variant: string;
}

export class LeadForm<
	StepFields extends PersonalLoanLeadStepFields = PersonalLoanLeadStepFields,
	Props extends PersonalLoanLeadProps = PersonalLoanLeadProps,
> extends BasicLeadForm<StepFields, Props> {
	public static defaultProps = {
		...BasicLeadForm.defaultProps,
		title: "Kérj ingyenes segítséget!",
	};

	protected initValues(): StepFields {
		return { ...super.initValues(), productRequisitionTime: undefined, wantsWithOnlineService: undefined };
	}

	protected renderFields(): ReactNode {
		const {
			submitState,
			showDetails = false,
			withInKhr = false,
			withRequisitionTimeOption = true,
			withOnlineOption = true,
		} = this.props;

		return (
			<>
				{showDetails && this.renderStepByStepHeader()}
				{super.renderFields()}
				{withRequisitionTimeOption && (
					<FormSelect
						name="productRequisitionTime"
						prepend={<FontAwesomeIcon icon={fasClock} />}
						options={PRODUCT_REQUISITION_TIME}
						placeholder="Mikor van szükséged a hitelre?"
						disabled={submitState !== undefined}
						required
					/>
				)}
				{withInKhr && (
					<FormDecidable
						name="inKHR"
						label="Szerepelsz KHR/BAR listán korábbi késedelmes fizetés miatt?"
						disabled={submitState !== undefined}
						required
					/>
				)}
				{withOnlineOption && (
					<FormCheck
						className="mb-1 font-weight-bold"
						label="Online szeretném megkötni"
						name="wantsWithOnlineService"
					/>
				)}
			</>
		);
	}

	private renderStepByStepHeader(): ReactNode {
		const { loanAmount, maturityYears, installment, apr, variant } = this.props;

		if (variant?.includes("details")) {
			return (
				<Row className="mt-2 mb-3 align-items-center">
					<Col sm={4} className="text-center">
						<FontAwesomeIcon icon={fasCheck} className="text-gold" size="4x" />
					</Col>
					<Col sm={8} className="h3 text-white">
						<strong>{formatText(loanAmount, "ft")}</strong> hitel,{" "}
						<strong>{formatText(maturityYears, "év")}</strong> futamidőre, havi{" "}
						<strong>{formatText(installment, "ft")}</strong>
						-tól, <strong>{formatText(apr, "%")}</strong> THM-mel.
					</Col>
				</Row>
			);
		}

		return (
			<Row className="mt-2 mb-3 align-items-center">
				<Col>
					<List itemStyle="tick" variant="gold" className="text-white">
						<List.Item>
							<strong>Ingyenes</strong> szolgáltatást, <strong>gyors</strong> ügyintézést biztosítunk.
						</List.Item>
						<List.Item>
							<strong>Átvállaljuk</strong> a papírmunkát!
						</List.Item>
					</List>
				</Col>
			</Row>
		);
	}

	protected renderPrivacyStatement(): ReactNode {
		return (
			<div className="text-center pt-2 mt-auto">
				Megismertem és hozzájárulok az{" "}
				<a
					className="text-reset text-decoration-underline"
					href={Config.getBankmonitorUrl("/adatvedelem/")}
					// biome-ignore lint: in-page link
					target="_blank"
				>
					Adatvédelmi szabályzatban
				</a>{" "}
				foglalt adatkezeléshez.
			</div>
		);
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.LeadForm`;
	}
}
